import useCustomers from '@/api/accounts-receivable/customers/useCustomers';
import useCustomer from '@/api/accounts-receivable/customers/useCustomer';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { useState, useMemo } from 'react';
import { T, useGT } from 'gt-react';
import CustomerPill from 'src/components/customer/CustomerPill';

/** Customers fetched per batch; the list grows a batch at a time. */
const CUSTOMER_BATCH_SIZE = 20;
/** The list endpoint's own ceiling — past this, searching is the way through. */
const MAX_CUSTOMER_PAGE_SIZE = 100;

export function CustomersAutocomplete({
  selectedCustomerId,
  onCustomerSelect,
  allowClear = false,
  className,
  disabled = false,
  sort = 'cjkLast',
}: {
  selectedCustomerId: string | undefined;
  onCustomerSelect: (customerId: string | undefined) => void;
  allowClear?: boolean;
  className?: string;
  /** Locks the picker, e.g. while the form around it is submitting. */
  disabled?: boolean;
  /**
   * List ordering. Defaults to 'cjkLast' so Latin-named customers lead and
   * CJK/pictographic names sink to the end of the dropdown.
   */
  sort?: 'name' | 'latinFirst' | 'cjkLast';
}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  // How many customers the dropdown has pulled in so far. Grows a batch at a
  // time as the user scrolls; a new search starts over from one batch.
  const [pageSize, setPageSize] = useState(CUSTOMER_BATCH_SIZE);
  const [searchAtLastReset, setSearchAtLastReset] = useState(search);
  if (search !== searchAtLastReset) {
    // Adjust-state-during-render (no effect), matching the pattern used
    // elsewhere for prop/state syncing.
    setSearchAtLastReset(search);
    setPageSize(CUSTOMER_BATCH_SIZE);
  }
  const { data: customersData, isFetching } = useCustomers({
    search: search || undefined,
    sort,
    pageSize,
  });

  // Fetch the selected customer to ensure it's always available for display
  const { data: selectedCustomerData } = useCustomer(selectedCustomerId);

  // Merge the selected customer into the options if it's not already there
  const options = useMemo(() => {
    const customers = customersData?.edges || [];

    // If there's a selected customer and it's not in the list, add it
    if (
      selectedCustomerData?.customer &&
      !customers.some((c) => c.id === selectedCustomerData.customer.id)
    ) {
      // Convert DetailedCustomerDTO to CustomerPreviewDTO format
      return [
        {
          id: selectedCustomerData.customer.id,
          name: selectedCustomerData.customer.name,
          erpDisplayName: selectedCustomerData.customer.erpDisplayName,
          aliases: selectedCustomerData.customer.aliases,
          address: selectedCustomerData.customer.address,
          phoneNumber: selectedCustomerData.customer.phoneNumber,
          emailDomain: selectedCustomerData.customer.emailDomain,
          contactPerson: selectedCustomerData.customer.contactPerson,
          paymentTerms: selectedCustomerData.customer.paymentTerms,
          creditLimit: selectedCustomerData.customer.creditLimit,
          creditLimitCurrency:
            selectedCustomerData.customer.creditLimitCurrency,
          isaId: selectedCustomerData.customer.isaId,
        },
        ...customers,
      ];
    }

    return customers;
  }, [customersData?.edges, selectedCustomerData]);

  const loadedCount = customersData?.edges.length ?? 0;
  const totalCount = customersData?.pageInfo.totalCount ?? 0;
  const unloaded = totalCount > loadedCount;

  return (
    <Autocomplete
      value={selectedCustomerId}
      getValue={(customer) => customer.id}
      getLabel={(customer) => customer.erpDisplayName ?? customer.name}
      options={options}
      searchValue={search}
      onSearchValueChange={setSearch}
      onValueChange={onCustomerSelect}
      placeholder={gt('Search customers', {
        context: 'customer search input placeholder',
      })}
      multiSelect={false}
      allowClear={allowClear}
      disabled={disabled}
      loadMore={{
        hasMore: unloaded && pageSize < MAX_CUSTOMER_PAGE_SIZE,
        isLoading: isFetching,
        onLoadMore: () =>
          setPageSize((current) =>
            Math.min(current + CUSTOMER_BATCH_SIZE, MAX_CUSTOMER_PAGE_SIZE),
          ),
        // At the endpoint's ceiling there are still matches we can't page to,
        // so say so rather than silently ending the list.
        exhaustedMessage: unloaded ? (
          <T context="customer autocomplete hint when more matches exist than can be listed">
            Keep typing to narrow these results
          </T>
        ) : null,
      }}
      className={className}
      renderTrigger={(customer) => (
        <CustomerPill
          customer={{
            name: customer.erpDisplayName ?? customer.name,
            emailDomain: customer.emailDomain ?? undefined,
            phoneNumber: customer.phoneNumber ?? undefined,
          }}
        />
      )}
    />
  );
}
